import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Signal } from "../../models/signal.ts";
import { getSignalLabelFromList } from "../../utils/signalUtils.ts";

export interface CommunicationSignalMappingRow {
  label: string;
  signal: Signal;
}

interface Props {
  title: string;
  rows: CommunicationSignalMappingRow[];
  signalOptions: Signal[];
  onSave: (signals: Signal[]) => void;
}

const hasStoredMapping = (signal: Signal): boolean => {
  const sourceType = signal.source_type ?? "unknown";
  const sourceId = signal.source_id ?? signal.id;
  const hasSignalText = signal.net.trim() !== "" || signal.name.trim() !== "";
  return hasSignalText && (sourceType !== "communication" || sourceId !== signal.id);
};

const UNRESOLVED_MAPPING_PREFIX = "__unresolved_mapping__:";

const unresolvedMappingIdFor = (signal: Signal): string =>
  `${UNRESOLVED_MAPPING_PREFIX}${signal.id}`;

const isUnresolvedMappingId = (targetId: string): boolean =>
  targetId.startsWith(UNRESOLVED_MAPPING_PREFIX);

const mappingLabelFor = (signal: Signal): string => {
  if (signal.net.trim() === "") {
    return signal.name;
  }
  if (signal.name.trim() === "") {
    return signal.net;
  }
  return `${signal.net} (${signal.name})`;
};

const targetIdFor = (signal: Signal, targets: Signal[]): string => {
  if (signal.source_id) {
    const sourceTarget = targets.find(
      (target) => (target.source_id ?? target.id) === signal.source_id,
    );
    if (sourceTarget) {
      return sourceTarget.id;
    }
  }
  return targets.find((target) => target.net === signal.net && target.name === signal.name)?.id ?? "";
};

const applyTarget = (source: Signal, targetId: string, targets: Signal[]): Signal => {
  const target = targets.find((option) => option.id === targetId);
  return target
    ? {
        ...source,
        net: target.net,
        name: target.name,
        source_type: target.source_type ?? "unknown",
        source_id: target.source_id ?? target.id,
      }
    : {
        ...source,
        net: "",
        name: "",
        source_type: "communication",
        source_id: source.id,
      };
};

const CommunicationSignalMappingGroup: React.FC<Props> = ({
  title,
  rows,
  signalOptions,
  onSave,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogRows, setDialogRows] = useState<CommunicationSignalMappingRow[]>([]);
  const [draftTargetIds, setDraftTargetIds] = useState<string[]>([]);

  const rowIds = useMemo(() => new Set(rows.map((row) => row.signal.id)), [rows]);
  const targetOptions = useMemo(
    () => signalOptions.filter((option) => !rowIds.has(option.id)),
    [rowIds, signalOptions],
  );
  const mappedRows = rows.filter((row) => hasStoredMapping(row.signal));
  const hasUnmappedRows = rows.some((row) => !hasStoredMapping(row.signal));

  const openEditor = () => {
    setDialogRows(rows);
    setDraftTargetIds(rows.map((row) => {
      const targetId = targetIdFor(row.signal, targetOptions);
      if (targetId !== "" || !hasStoredMapping(row.signal)) {
        return targetId;
      }
      return unresolvedMappingIdFor(row.signal);
    }));
    setOpenDialog(true);
  };

  const closeEditor = () => {
    setOpenDialog(false);
    setDialogRows([]);
    setDraftTargetIds([]);
  };

  const handleSave = () => {
    const updatedById = new Map(
      dialogRows.map((row, index) => [
        row.signal.id,
        isUnresolvedMappingId(draftTargetIds[index] ?? "")
          ? row.signal
          : applyTarget(row.signal, draftTargetIds[index] ?? "", targetOptions),
      ]),
    );
    onSave(rows.map((row) => updatedById.get(row.signal.id) ?? row.signal));
    closeEditor();
  };

  const handleDelete = (deletedRow: CommunicationSignalMappingRow) => {
    onSave(rows.map((row) =>
      row.signal.id === deletedRow.signal.id
        ? {
            ...row.signal,
            net: "",
            name: "",
            source_type: "communication",
            source_id: row.signal.id,
          }
        : row.signal
    ));
  };

  return (
    <Box mt={2}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      {mappedRows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Signal</TableCell>
              <TableCell>Zugeordnetes Netz</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mappedRows.map((row) => (
              <TableRow key={row.signal.id}>
                <TableCell>{row.label}</TableCell>
                <TableCell>{mappingLabelFor(row.signal)}</TableCell>
                <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={openEditor}
                    >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(row)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Box mt={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={openEditor}
          disabled={targetOptions.length === 0 || !hasUnmappedRows}
        >
          Hinzufügen
        </Button>
      </Box>
      <Dialog open={openDialog} onClose={closeEditor} fullWidth maxWidth="sm">
        <DialogTitle>{title} zuordnen</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{title}</TableCell>
                <TableCell>Zielsignal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dialogRows.map((row, index) => (
                <TableRow key={row.signal.id}>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={draftTargetIds[index] ?? ""}
                      onChange={(event) => {
                        const updated = [...draftTargetIds];
                        updated[index] = event.target.value;
                        setDraftTargetIds(updated);
                      }}
                    >
                      <MenuItem value="">Keine Zuordnung</MenuItem>
                      {isUnresolvedMappingId(draftTargetIds[index] ?? "") && (
                        <MenuItem value={draftTargetIds[index]} disabled>
                          Nicht mehr in Signalliste: {mappingLabelFor(row.signal)}
                        </MenuItem>
                      )}
                      {targetOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {getSignalLabelFromList(option.id, targetOptions)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box mt={2} display="flex" gap={2}>
            <Button variant="contained" onClick={handleSave}>Speichern</Button>
            <Button variant="outlined" onClick={closeEditor}>Abbrechen</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CommunicationSignalMappingGroup;
